import stepCommonClassName from '@/app/_lens-components/style/step-common';
import { EPrescriptionType } from '@/utils/enums/goods';
import { RightArrowOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import Image from '@/components/Image';
import { EPlatform } from '@/utils/enums/platform-enum';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { useStore } from '@/providers/app-store-provider';
import useTrackEditPrescription from '../services/useTrackEditPrescription';
import { useInView } from 'react-intersection-observer';
import {
  type PrescriptionsMethod,
  useLensProcessStore,
} from '@/app/_lens-components/store/lens-process-store';
import { PRESCRIPTION_METHODS } from '@/app/_lens-components/constants';
import { useOpticalProcedureTitle } from '@/app/_lens-components/copywriting/useOpticalProcedureTitle';

interface IProps {
  prescriptionType: EPrescriptionType;
  onSelectMethod: (method: PrescriptionsMethod) => void;
  onClickSignIn: () => void;
  isStepActive?: boolean;
}
export default function FillOutPrescriptionMethods({
  prescriptionType,
  onSelectMethod,
  onClickSignIn,
  isStepActive = false,
}: IProps) {
  const t = useTranslations('lens-process');
  const prescriptionMethodsTitle = useOpticalProcedureTitle(
    'prescription_methods',
    t('prescriptionMethodsTitle'),
  );
  const waiMode = useStore((state) => state.waiMode);
  const setSelectedPrescription = useLensProcessStore((state) => state.setSelectedPrescription);
  const selectedMethod = useLensProcessStore((state) => state.selectedMethod);
  const { methods, prescriptionTypesCount } = useLensProcessStore(
    (state) => state.prescriptionFillMethodsData,
  );

  useEffect(() => {
    if (isStepActive) {
      setSelectedPrescription(null);
    }
  }, [isStepActive, setSelectedPrescription]);
  const { deviceType } = useContext(ResponsiveContext);
  const token = useStore((state) => state.token);
  const userInfo = useStore((state) => state.userInfo);

  const isWap = deviceType === EPlatform.WAP;
  const { trackPrescriptionMethodsView } = useTrackEditPrescription();
  const methodsContainerRef = useRef<HTMLDivElement | null>(null);

  // 检查是否已登录
  const isLogin = Boolean(token && userInfo?.id);
  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });
  useEffect(() => {
    if (inView) {
      trackPrescriptionMethodsView();
    }
  }, [inView]);

  useLayoutEffect(() => {
    if (!isStepActive || !selectedMethod?.type) return;
    const selectedEl = methodsContainerRef.current?.querySelector<HTMLElement>(
      `[data-prescription-method="${selectedMethod.type}"]`,
    );
    selectedEl?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
      inline: 'nearest',
    });
  }, [isStepActive, selectedMethod?.type]);
  const checkIsDisabled = (item: PrescriptionsMethod) => {
    if (item.type === PRESCRIPTION_METHODS.CHOOSE_PRESCRIPTION) {
      return (prescriptionTypesCount[getPrescriptionType(prescriptionType)] || 0) === 0;
    }
    return item.status === 2;
  };
  // * 获取真实处方类型， reading prescription 不是常规处方类型
  const getPrescriptionType = (presTypeArg: EPrescriptionType) => {
    if (presTypeArg === EPrescriptionType.PRESCRIPTION_READING) {
      return EPrescriptionType.READING_GLASSES;
    }
    return presTypeArg;
  };
  const presType = getPrescriptionType(prescriptionType);
  const prescriptionTypeCount = prescriptionTypesCount[presType];
  const disabledMethodStyle = {
    filter: 'grayscale(100%) brightness(0.8) contrast(0.9)',
    opacity: 0.6,
  };
  // 优先展示未被禁用的
  const prescriptionMethods = methods.slice().sort((a, b) => a.status - b.status);
  const myPrescriptionMethodOrder = prescriptionMethods.findIndex(
    (item) => item.type === PRESCRIPTION_METHODS.CHOOSE_PRESCRIPTION,
  );
  if (myPrescriptionMethodOrder !== -1 && prescriptionTypesCount[presType] === 0) {
    prescriptionMethods.push(prescriptionMethods.splice(myPrescriptionMethodOrder, 1)[0]);
  }
  if (!isLogin) {
    prescriptionMethods.splice(
      prescriptionMethods.findIndex(
        (item) => item.type === PRESCRIPTION_METHODS.CHOOSE_PRESCRIPTION,
      ),
      1,
    );
  }
  const onSelectPreMethod = (item: PrescriptionsMethod) => {
    if (
      item.status === 2 ||
      (item.type === PRESCRIPTION_METHODS.CHOOSE_PRESCRIPTION && prescriptionTypeCount === 0)
    ) {
      return;
    }
    onSelectMethod(item);
  };
  return (
    <div className="font-br" ref={ref}>
      <div className={stepCommonClassName.stepTitle}>
        <span>{prescriptionMethodsTitle}</span>
      </div>
      <div className="flex flex-col max-xl:px-[14px] pt-1" ref={methodsContainerRef}>
        {prescriptionMethods?.map((item) => (
          <div
            key={item.title}
            data-prescription-method={item.type}
            className={`${stepCommonClassName.prescriptionTypeOption} ${checkIsDisabled(item) ? 'hover:border-[white]' : ''} ${
              selectedMethod?.type === item.type
                ? stepCommonClassName.prescriptionTypeOptionSelected
                : ''
            }`}
            onClick={() => onSelectPreMethod(item)}
          >
            <div className={stepCommonClassName.prescriptionTypeContent}>
              <Image
                src={item.img_url}
                alt={item.title}
                width={48}
                height={48}
                className={stepCommonClassName.prescriptionTypeImage}
                style={checkIsDisabled(item) ? disabledMethodStyle : {}}
              />
              <div className={`${stepCommonClassName.prescriptionTypeTextContainer} `}>
                <div className={stepCommonClassName.prescriptionTypeTitle}>
                  <span
                    className={`${stepCommonClassName.prescriptionTypeTitleText} ${checkIsDisabled(item) ? 'text-[#333333] opacity-50' : ''}`}
                  >
                    {item.title}
                    {item.type === PRESCRIPTION_METHODS.CHOOSE_PRESCRIPTION ? (
                      <span className="ml-4">{t('youHave', { count: prescriptionTypeCount })}</span>
                    ) : (
                      ''
                    )}
                  </span>
                </div>
                <span
                  className={`${stepCommonClassName.prescriptionTypeDescription(waiMode)} ${checkIsDisabled(item) ? 'text-[#666666] opacity-50' : ''}`}
                >
                  {item.description}
                </span>
              </div>
              {isWap && (
                <div className={`${stepCommonClassName.prescriptionTypeArrow}`}>
                  <RightArrowOutlined
                    className={`${stepCommonClassName.arrowIcon}
                    ${checkIsDisabled(item) ? 'opacity-50' : ''}
                      `}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {!isLogin && (
        <div className="text-center text-[12px]/[16px] text-[#282828] mt-[20px]">
          {t('haveASavedPrescription')}
          <span className="underline cursor-pointer ml-2" onClick={onClickSignIn}>
            {t('signIn')}
          </span>
        </div>
      )}
    </div>
  );
}
